import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

type TimerStatus = 'ready' | 'running' | 'paused' | 'done';

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Review fix F11: the public sample's quiet timer (no audio). Elapsed time is derived from a
 * monotonic clock (performance.now), not by counting interval ticks, so a throttled background
 * tab can't make it drift. State changes are announced once via a polite live region; the ticking
 * clock itself (role="timer") is deliberately not live. */
export function SilentTimer({ durationSeconds }: { durationSeconds: number }) {
  const { t } = useTranslation();
  const totalMs = Math.max(1, durationSeconds) * 1000;
  const [status, setStatus] = useState<TimerStatus>('ready');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const baseElapsedRef = useRef(0);
  const startedAtRef = useRef(0);

  useEffect(() => {
    if (status !== 'running') return;
    const tick = () => {
      const elapsed = baseElapsedRef.current + (performance.now() - startedAtRef.current);
      if (elapsed >= totalMs) {
        baseElapsedRef.current = totalMs;
        setElapsedMs(totalMs);
        setStatus('done');
        setAnnouncement(t('public.sample.timer.done'));
        return;
      }
      setElapsedMs(elapsed);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [status, totalMs, t]);

  const start = () => {
    startedAtRef.current = performance.now();
    setStatus('running');
    setAnnouncement(t('public.sample.timer.started'));
  };
  const pause = () => {
    baseElapsedRef.current += performance.now() - startedAtRef.current;
    setElapsedMs(baseElapsedRef.current);
    setStatus('paused');
    setAnnouncement(t('public.sample.timer.paused'));
  };
  const reset = () => {
    baseElapsedRef.current = 0;
    setElapsedMs(0);
    setStatus('ready');
    setAnnouncement(t('public.sample.timer.reset'));
  };

  const remaining = Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000));
  const clock = formatClock(remaining);
  const progress = Math.min(100, (elapsedMs / totalMs) * 100);
  const faceStyle = { ['--progress' as string]: progress.toFixed(1) } as CSSProperties;

  return (
    <section className="sample-timer" aria-labelledby="sample-timer-heading">
      <h2 id="sample-timer-heading">{t('public.sample.timer.heading')}</h2>
      <div className="sample-timer-face" style={faceStyle}>
        <div className="sample-timer-inner">
          <span className="sample-timer-time" role="timer" aria-label={`${t('public.sample.timer.remaining')}: ${clock}`}>
            {clock}
          </span>
        </div>
      </div>
      <div className="sample-timer-controls">
        {status === 'running' ? (
          <button type="button" className="btn btn-primary" onClick={pause}>{t('public.sample.timer.pause')}</button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={status === 'done' ? reset : start}>
            {status === 'paused' ? t('public.sample.timer.resume') : status === 'done' ? t('public.sample.timer.again') : t('public.sample.timer.start')}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={reset} disabled={status === 'ready'}>
          {t('public.sample.timer.resetButton')}
        </button>
      </div>
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
    </section>
  );
}
